import { Box } from "@mui/material";
import { PageTitleBar } from "common/components";
import { useTranslation } from "common/locales";
import FieldsList from "features/fieldList/FieldsList";
import {
  ArableField,
  Field,
  getAllFieldsForSilage,
  getArableFields,
  getGrainFields,
  selectCurrentFarm,
} from "model";
import { useAppSelector } from "model/rootReducer";
import { useHistory, useParams } from "react-router-dom";
import styled from "styled-components";

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  background-color: #f7fcf1;
  -webkit-overflow-scrolling: touch;
  padding-top: 16px;
  padding-bottom: calc(env(safe-area-inset-bottom));
`;

export default function FieldsListScreen() {
  const { fieldType } = useParams<{ fieldType: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const currentFarm = useAppSelector(selectCurrentFarm);

  const handleBackToFarm = () => {
    if (currentFarm?.guid) {
      history.push(`/farm/${currentFarm.guid}`);
    }
  };

  // Get all field types at the top level
  const grainFields = useAppSelector(getGrainFields).filter(
    (f): f is ArableField => f !== undefined
  );
  const arableFields = useAppSelector(getArableFields).filter(
    (f): f is ArableField => f !== undefined
  );
  const silageFields = useAppSelector(getAllFieldsForSilage).filter(
    (f): f is Field => f !== undefined
  );

  // Get fields based on type
  const getFields = () => {
    const type = fieldType.toLowerCase();

    // Use exact matches for field types
    switch (type) {
      case "grain":
        return grainFields;
      case "arable":
        return arableFields;
      case "silage":
        return silageFields;
      default:
        return [];
    }
  };

  const fields = getFields();

  const getFieldIds = () => {
    const ids = fields.map((field) => field.guid);
    return ids;
  };

  // Get the translation key based on the field type
  const getTranslationKey = () => {
    // Use exact matches for translation keys
    switch (fieldType) {
      case "grain":
      case "arable":
      case "silage":
      case "pasture":
        return fieldType;
      default:
        console.warn("Unknown translation key for type:", fieldType);
        return fieldType;
    }
  };

  const title = t(`fieldTypeCards.${getTranslationKey()}.title`);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <PageTitleBar title={title} onBack={handleBackToFarm} />
      <Content>
        <FieldsList
          fieldIds={getFieldIds()}
          emptyText={t("fieldTypeCards.noFields")}
        />
      </Content>
    </Box>
  );
}
